import { useTranslation } from 'react-i18next';
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import React, { useState, useEffect } from 'react';
import { getAuditLogs, getAuditLogTables, getAuditLogOperations, exportAuditLogsToCSV } from '../../api/auditLogs';
import { getUsers } from '../../api/users';
import { formatLocalDateTime, getDateTimeFormatPlaceholder } from '../../utils/date';
import DateTimePicker from '../../components/common/DateTimePicker';
import ListPageLayout, { ListPageHeader, ListLoadingSpinner } from '../../components/layout/ListPageLayout';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import { apiErrorMessage } from '../../utils/apiError';

const AuditLogs = () => {
  const { t } = useTranslation();
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [tables, setTables] = useState([]);
  const [operations, setOperations] = useState([]);
  const [filters, setFilters] = useState({
    userId: '',
    tableName: '',
    operation: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filtersLoadError, setFiltersLoadError] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortKey, setSortKey] = useState('id');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const serverSortBy = {
    id: 'id',
    tableName: 'tableName',
    operation: 'operation',
    recordId: 'recordId',
    user: 'userId',
    created: 'created',
  }[sortKey] || 'id';

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response || []);
      return true;
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
      return false;
    }
  };

  const fetchTables = async () => {
    try {
      const response = await getAuditLogTables();
      setTables(response || []);
      return true;
    } catch (err) {
      console.error('Failed to fetch tables:', err);
      setTables([]);
      return false;
    }
  };

  const fetchOperations = async () => {
    try {
      const response = await getAuditLogOperations();
      setOperations(response || []);
      return true;
    } catch (err) {
      console.error('Failed to fetch operations:', err);
      setOperations([]);
      return false;
    }
  };

  useEffect(() => {
    (async () => {
      const results = await Promise.all([fetchUsers(), fetchTables(), fetchOperations()]);
      setFiltersLoadError(results.some((ok) => !ok));
    })();
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [page, rowsPerPage, filters, sortKey, sortDir]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('fetchAuditLogs called with filters:', filters);
      const params = {
        page,
        size: rowsPerPage,
        sortBy: serverSortBy,
        sortDir: sortDir.toUpperCase(),
      };

      // Add filters one by one, processing userId specially
      if (filters.userId && filters.userId !== '') {
        const userIdInt = parseInt(filters.userId, 10);
        console.log('Processing userId:', filters.userId, '-> parsed:', userIdInt);
        if (!isNaN(userIdInt)) {
          params.userId = userIdInt;
        }
      }

      if (filters.tableName && filters.tableName !== '') {
        params.tableName = filters.tableName;
      }

      if (filters.operation && filters.operation !== '') {
        params.operation = filters.operation;
      }

      if (filters.startDate && filters.startDate !== '') {
        console.log('startDate filter value:', filters.startDate);
        params.fromDate = filters.startDate;
      }

      if (filters.endDate && filters.endDate !== '') {
        console.log('endDate filter value:', filters.endDate);
        params.toDate = filters.endDate;
      }

      if (filters.search && filters.search !== '') {
        params.search = filters.search;
      }

      console.log('Final params being sent to API:', params);
      const response = await getAuditLogs(params);
      setAuditLogs(response.content || []);
      setTotalElements(response.totalElements || 0);
    } catch (err) {
      setError(apiErrorMessage(err, t('pages.auditLogs.fetchFailed')));
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    console.log(`Filter changed: ${field} = ${value}`);
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      userId: '',
      tableName: '',
      operation: '',
      startDate: '',
      endDate: '',
      search: ''
    });
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const params = {};

      // Add filters one by one, processing userId specially
      if (filters.userId && filters.userId !== '') {
        const userIdInt = parseInt(filters.userId, 10);
        if (!isNaN(userIdInt)) {
          params.userId = userIdInt;
        }
      }

      if (filters.tableName && filters.tableName !== '') {
        params.tableName = filters.tableName;
      }

      if (filters.operation && filters.operation !== '') {
        params.operation = filters.operation;
      }

      if (filters.startDate && filters.startDate !== '') {
        params.fromDate = filters.startDate;
      }

      if (filters.endDate && filters.endDate !== '') {
        params.toDate = filters.endDate;
      }

      if (filters.search && filters.search !== '') {
        params.search = filters.search;
      }

      params.page = 0;
      params.size = 5000;

      const response = await getAuditLogs(params);
      exportAuditLogsToCSV(response.content || []);
    } catch (err) {
      setError(t('pages.auditLogs.exportFailed'));
      console.error('Error exporting audit logs:', err);
    }
  };

  const getOperationColor = (operation) => {
    const colors = {
      'INSERT': 'bg-green-100 text-green-800',
      'UPDATE': 'bg-blue-100 text-blue-800',
      'DELETE': 'bg-red-100 text-red-800',
      'LOGIN': 'bg-purple-100 text-purple-800',
      'LOGOUT': 'bg-gray-100 text-gray-800',
      'PASSWORD_SET': 'bg-yellow-100 text-yellow-800',
      'PASSWORD_RESET': 'bg-orange-100 text-orange-800',
      'BULK_UPDATE': 'bg-indigo-100 text-indigo-800',
      'ARCHIVE': 'bg-amber-100 text-amber-800',
      'ACTIVATE': 'bg-emerald-100 text-emerald-800',
      'DEACTIVATE': 'bg-slate-100 text-slate-800'
    };
    return colors[operation] || 'bg-gray-100 text-gray-800';
  };

  const formatJSON = (jsonString) => {
    if (!jsonString) return t('common.na', { defaultValue: 'N/A' });
    try {
      const obj = JSON.parse(jsonString);
      return JSON.stringify(obj, null, 2);
    } catch {
      return jsonString;
    }
  };

  const operationLabel = (op) => {
    if (!op) return t('common.na', { defaultValue: 'N/A' });
    return t(`pages.auditLogs.ops.${op}`, { defaultValue: String(op).replace(/_/g, ' ') });
  };

  const tableLabel = (name) => {
    if (!name) return t('common.na', { defaultValue: 'N/A' });
    return t(`pages.auditLogs.tables.${name}`, { defaultValue: String(name).replace(/_/g, ' ') });
  };

  const totalPages = Math.ceil(totalElements / rowsPerPage);

  return (
    <ListPageLayout>
      <ListPageHeader
        title={t('pages.auditLogs.title')}
        subtitle={t('pages.auditLogs.subtitle')}
      />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        {filtersLoadError && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
            {t('common.optionsLoadFailed')}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pages.auditLogs.filterUser')}</label>
            <select
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('pages.auditLogs.allUsers')}</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pages.auditLogs.filterTable')}</label>
            <select
              value={filters.tableName}
              onChange={(e) => handleFilterChange('tableName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('pages.auditLogs.allTables')}</option>
              {tables.map(table => (
                <option key={table} value={table}>{tableLabel(table)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pages.auditLogs.filterOperation')}</label>
            <select
              value={filters.operation}
              onChange={(e) => handleFilterChange('operation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('pages.auditLogs.allOperations')}</option>
              {operations.map(op => (
                <option key={op} value={op}>{operationLabel(op)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pages.auditLogs.filterStartDate')}</label>
            <DateTimePicker
              value={filters.startDate}
              onChange={(value) => handleFilterChange('startDate', value)}
              placeholder={getDateTimeFormatPlaceholder()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pages.auditLogs.filterEndDate')}</label>
            <DateTimePicker
              value={filters.endDate}
              onChange={(value) => handleFilterChange('endDate', value)}
              placeholder={getDateTimeFormatPlaceholder()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pages.auditLogs.filterSearch')}</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder={t('pages.auditLogs.searchPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={clearFilters}
            className="btn-page-action-secondary"
          >
            {t('pages.auditLogs.clearFilters')}
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="btn-page-action-export"
          >
            {t('pages.auditLogs.exportCsv')}
          </button>
          <ExcelDownloadButton
            columns={[
              { header: t('common.idColumn', { defaultValue: 'ID' }), key: 'id', width: 8 },
              { header: t('pages.auditLogs.colTable'), key: 'tableName', render: (v) => tableLabel(v), width: 20 },
              { header: t('pages.auditLogs.colOperation'), key: 'operation', render: (v) => operationLabel(v), width: 12 },
              { header: t('pages.auditLogs.colRecordId'), key: 'recordId', width: 12 },
              { header: t('exports.userId', { defaultValue: 'User ID' }), key: 'userId', width: 10 },
              {
                header: t('pages.auditLogs.filterUser'),
                key: 'userName',
                render: (v, row) => [v, row.userId ? `(${row.userId})` : ''].filter(Boolean).join(' ') || t('common.system'),
                width: 22,
              },
              { header: t('pages.auditLogs.colTimestamp'), key: 'created', width: 22 },
            ]}
            data={auditLogs}
            filename="audit-logs"
            disabled={auditLogs.length === 0}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <ListLoadingSpinner />
        ) : auditLogs.length === 0 && !error ? (
          <div className="p-8 text-center text-gray-500">{t('pages.auditLogs.empty')}</div>
        ) : auditLogs.length === 0 ? null : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="table-header">
                <tr>
                  <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader label={t('pages.auditLogs.colTable')} columnKey="tableName" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader label={t('pages.auditLogs.colOperation')} columnKey="operation" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader label={t('pages.auditLogs.colRecordId')} columnKey="recordId" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader label={t('pages.auditLogs.filterUser')} columnKey="user" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader label={t('pages.auditLogs.colTimestamp')} columnKey="created" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th className="table-header-cell">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {auditLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="table-row">
                      <td className="table-cell whitespace-nowrap">{log.id}</td>
                      <td className="table-cell whitespace-nowrap">{tableLabel(log.tableName)}</td>
                      <td className="table-cell whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getOperationColor(log.operation)}`}>
                          {operationLabel(log.operation)}
                        </span>
                      </td>
                      <td className="table-cell whitespace-nowrap">{log.recordId || t('common.na', { defaultValue: 'N/A' })}</td>
                      <td className="table-cell whitespace-nowrap">
                        {log.userName || t('common.system')} {log.userId && `(${log.userId})`}
                      </td>
                      <td className="table-cell whitespace-nowrap text-gray-500">
                        {formatLocalDateTime(log.created)}
                      </td>
                      <td className="table-cell whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                          className="btn-action-view"
                        >
                          {expandedRow === log.id ? t('common.hideDetails') : t('common.showDetails')}
                        </button>
                      </td>
                    </tr>
                    {expandedRow === log.id && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('pages.auditLogs.oldData')}</h4>
                              <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-x-auto">
                                {formatJSON(log.oldData)}
                              </pre>
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('pages.auditLogs.newData')}</h4>
                              <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-x-auto">
                                {formatJSON(log.newData)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="bg-gray-50 px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-700">
              {t('pages.auditLogs.showingEntries', { start: page * rowsPerPage + 1, end: Math.min((page + 1) * rowsPerPage, totalElements), total: totalElements })}
            </span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.firstPage')}
            </button>
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.previousPage')}
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              {t('common.pageOf', { current: page + 1, total: totalPages || 1 })}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.nextPage')}
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.lastPage')}
            </button>
          </div>
        </div>
      </div>
    </ListPageLayout>
  );
};

export default AuditLogs;
